import { useState } from "react";
import { useApi, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtNum, fmtPct } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const MODELS = ["gpt-5.5", "gpt-5.4-mini", "claude-opus-4-7", "claude-sonnet-4-6"];
const CACHES = ["none", "prompt", "semantic"];
const EMPTY = { name: "", model: "gpt-5.5", fallback: "claude-sonnet-4-6", tier: 2, cache: "semantic" };

export default function Gateway() {
  const { data, isLoading, error, refetch } = useApi("gateway", "/gateway/metrics");
  const [editing, setEditing] = useState(null); // route obj or "new" or null
  const [form, setForm] = useState(EMPTY);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (r) => { setForm({ name: r.name, model: r.model, fallback: r.fallback, tier: r.tier, cache: r.cache, id: r.id }); setEditing(r); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Route name is required");
    if (editing === "new") { await apiPost("/gateway/routes", form); toast.success(`Route "${form.name}" created`); }
    else { await apiPatch(`/gateway/routes/${form.id}`, form); toast.success(`Route "${form.name}" updated`); }
    setEditing(null); refetch();
  };
  const remove = async (r) => { await apiDelete(`/gateway/routes/${r.id}`); toast.success(`Route "${r.name}" deleted`); refetch(); };

  if (error) return <ErrorState />;
  const s = data?.summary;
  return (
    <div>
      <PageHeader eyebrow="Navitra Gate" title="Gateway" subtitle="Traffic, latency, errors, cache and router decisions across virtual-key routes.">
        <Button size="sm" onClick={openNew} data-testid="new-route"><Plus className="h-4 w-4 mr-1.5" />New route</Button>
      </PageHeader>
      {isLoading || !s ? <LoadingBlock /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KpiTile testid="rps" label="Requests / sec" value={fmtNum(s.rps)} explain={{ formula: "Σ rps across routes", sources: [{ id: "Gate", name: "AI Gateway" }] }} />
            <KpiTile testid="p95" accent label="p95 latency" value={`${s.p95_ms} ms`} sub="added by routing + DLP" explain={{ formula: "avg p95 across routes", sources: [{ id: "Gate", name: "AI Gateway" }] }} />
            <KpiTile testid="cache" label="Cache hit rate" value={fmtPct(s.cache_hit)} explain={{ formula: "cached_input_tokens / total_input_tokens", sources: [{ id: "Gate", name: "Semantic cache" }] }} />
            <KpiTile testid="err" label="Error rate" value={fmtPct(s.error_rate, 1)} explain={{ formula: "errors / requests", sources: [{ id: "Gate", name: "AI Gateway" }] }} />
          </div>
          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Routes ({data.routes.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Route</TableHead><TableHead>Model</TableHead><TableHead>Fallback</TableHead><TableHead>Cache</TableHead><TableHead className="text-right">RPS</TableHead><TableHead className="text-right">p95</TableHead><TableHead className="text-right">Cache hit</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.routes.map((r) => (
                    <TableRow key={r.id} data-testid={`route-${r.id}`}>
                      <TableCell className="font-medium mono text-sm">{r.name}</TableCell>
                      <TableCell className="mono text-sm">{r.model}</TableCell>
                      <TableCell className="mono text-xs text-muted-foreground">{r.fallback || "—"}</TableCell>
                      <TableCell><SeverityChip level={r.cache === "none" ? "neutral" : "info"} label={r.cache} /></TableCell>
                      <TableCell className="text-right mono">{r.rps}</TableCell>
                      <TableCell className="text-right mono">{r.p95_ms} ms</TableCell>
                      <TableCell className="text-right mono">{fmtPct(r.cache_hit)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`delete-${r.id}`} className="text-sev-danger hover:text-sev-danger"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="route-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing === "new" ? "New route" : "Edit route"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Route name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. support-bot" data-testid="route-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Primary model</Label>
                <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}><SelectTrigger data-testid="route-model"><SelectValue /></SelectTrigger><SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Fallback model</Label>
                <Select value={form.fallback} onValueChange={(v) => setForm({ ...form, fallback: v })}><SelectTrigger data-testid="route-fallback"><SelectValue /></SelectTrigger><SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Tier</Label>
                <Select value={String(form.tier)} onValueChange={(v) => setForm({ ...form, tier: Number(v) })}><SelectTrigger data-testid="route-tier"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>Tier {t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Cache</Label>
                <Select value={form.cache} onValueChange={(v) => setForm({ ...form, cache: v })}><SelectTrigger data-testid="route-cache"><SelectValue /></SelectTrigger><SelectContent>{CACHES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-route">{editing === "new" ? "Create route" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
